import type { RoomDTO } from "@/modules/master/domain/contracts";
import { formatDate } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import {
  Badge,
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EditIcon, TrashIcon } from "lucide-react";

type RoomRow = RoomDTO;

type RoomsTableProps = {
  data: RoomRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit: (room: RoomRow) => void;
  onDelete: (id: string) => void;
};

export function RoomsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onDelete,
}: RoomsTableProps) {
  const columns: TanstackReactTable.ColumnDef<RoomRow>[] = [
    createSortableColumn("name", "ຊື່"),
    {
      header: "ຄວາມຈຸ",
      accessorKey: "capacity",
      cell: ({ row }) =>
        row.original.capacity ? `${row.original.capacity} ຄົນ` : "-",
    },
    {
      header: "ຄຳອະທິບາຍ",
      accessorKey: "description",
      cell: ({ row }) => (
        <div className="max-w-md truncate">
          {row.original.description || "-"}
        </div>
      ),
    },
    {
      header: "ສະຖານະ",
      accessorKey: "status",
      cell: ({ row }) => {
        const status = row.original.status;
        const variant =
          status === "active"
            ? "default"
            : status === "maintenance"
              ? "secondary"
              : "outline";
        const label =
          status === "active"
            ? "ໃຊ້ງານ"
            : status === "maintenance"
              ? "ກຳລັງບຳລຸງຮັກສາ"
              : "ບໍ່ໃຊ້ງານ";
        return <Badge variant={variant}>{label}</Badge>;
      },
    },
    {
      header: "ວັນທີສ້າງ",
      accessorKey: "createdAt",
      cell: ({ row }) => formatDate(row.original.createdAt),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }: { row: { original: RoomRow } }) => {
        const { id } = row.original;
        const actions = [];

        actions.push({
          label: "ແກ້ໄຂ",
          icon: <EditIcon className="h-4 w-4" />,
          onClick: () => onEdit(row.original),
        });

        actions.push({
          label: "ລຶບ",
          variant: "destructive" as const,
          icon: <TrashIcon className="h-4 w-4" />,
          onClick: async () => {
            const ok = await confirm({
              title: "ລຶບຫ້ອງຮຽນ",
              description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບຫ້ອງຮຽນນີ້?",
              actionText: "ລຶບ",
              ActionProps: {
                variant: "destructive",
              },
            });
            if (ok) onDelete(id);
          },
        });

        return <RowActions actions={actions} maxInline={2} />;
      },
    },
  ];

  return (
    <DataTable<RoomRow, unknown>
      noDataMessage="ບໍ່ພົບຫ້ອງຮຽນ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
